import { /*mapStyles,*/ markerIcon, markerFromImage, haversine_distance } from 'helpers/maps';
import { AnyMapItem, IMapItem } from 'models/map';
import { IOffice, IOfficeListItem, IOfficeTime } from 'models/shop/office';
import * as React from 'react';
import * as i18n from 'i18next';
import { lowerFirstObjectPropertiesNames, readURLSearchParams } from 'helpers/common';
import * as c from 'models/constants';
import * as ReactDOMServer from 'react-dom/server';
import ReviewScore from 'components/FormControls/ReviewScore';
import renderIcon from 'helpers/renderIcon';
import moment = require('moment');
import { formatWorkTime } from 'helpers/bookingTemplates';

function destroyMap(map: google.maps.Map) {
	map?.unbindAll();
}

interface IProps<TItem extends AnyMapItem> {
	userPicture: string;
	initLocation?: google.maps.LatLng | google.maps.LatLngLiteral;
	className?: string;
	items: TItem[];
	mapSettings?: google.maps.MapOptions;
	triggerMapCenter?: { currentOfficeId: string };
	geoLatitude?: number;
	geoLongitude?: number;
	showInfoWindow?: boolean;
	numberOfActiveFilters?: number;

	/** Geomap functions */
	onClick?: (item: TItem) => void;
	onSelectClick?: () => void;
	// onMarkerMouseEnter?: (item: TItem) => void;
	// onMarkerMouseLeave?: (item: TItem) => void;
	onMapMove?: (bounds: google.maps.LatLngBounds) => void;
	onMapDrawn?: (bounds: google.maps.LatLngBounds) => void;
	openFiltersModal?: () => void;
}

const isOffice = (item: AnyMapItem): item is IOffice | IOfficeListItem => 'geoLocationLatitude' in item;

const normalizeItem = <TItem extends AnyMapItem>(item: TItem): IMapItem<TItem> => {
	if (isOffice(item)) {
		return {
			latitude: item.geoLocationLatitude,
			longitude: item.geoLocationLongitude,
			title: 'shop' in item ? item?.shop?.name || item.name : item.name,
			image: 'shop' in item ? item?.shop?.picture || '' : undefined,
			isHighlighted: item.isHighlighted,
		};
	}
	if ('latitude' in item) {
		return item as IMapItem<any>;
	}
};

interface MarkerMap {
	itemJson: string;
	marker: google.maps.marker.AdvancedMarkerElement;
	isHighlighted: boolean;
}

const jsonIdentifier = (item: IMapItem): string => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { isHighlighted, ...smallItem } = item;
	return JSON.stringify(smallItem);
};

const itemIsIOffice = (i: AnyMapItem): i is IOffice => 'geoLocationLatitude' in i;

const getBase64Image = (url) =>
	new Promise((resolve) => {
		const img = new Image();
		img.setAttribute('crossOrigin', 'anonymus');
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			ctx.drawImage(img, 0, 0);
			const dataURL = canvas.toDataURL('image/png');
			resolve(dataURL);
		};
		img.src = url + `?teststststs=2`;
	});

const getMarkerInfoWindowContent = (item: any): string => {
	let distance: JSX.Element = null;
	if ('origin' in item && item.origin && item.geoLocationLatitude && item.geoLocationLongitude) {
		const d = haversine_distance(
			item.origin.latitude,
			item.origin.longitude,
			item.geoLocationLatitude,
			item.geoLocationLongitude,
		);
		distance = <>{d.toFixed(1)} km</>;
	}

	let times: IOfficeTime[];
	try {
		if (item?.isFromShopPage) {
			times = item.officeTimes;
		} else {
			times = JSON.parse(item.officeTimes || '[]').map(lowerFirstObjectPropertiesNames);
		}
	} catch (e) {
		console.error(e);
	}

	const workTime = (times || []).find((time) => time.day == moment(new Date()).day());
	const formattedWorkTime = workTime
		? formatWorkTime(workTime)
		: i18n.t('Shop_ManageShop_Location_Label_OpeningTimes_Closed');

	return ReactDOMServer.renderToString(
		<div className="markerInfoWindowWrapper">
			<div className="markerInfoWindow">
				<div className="officeInfo">
					<h2>{item?.shop?.name}</h2>
					<div className="details-row">
						<div className="rating">
							<ReviewScore averageRating={item.shopAverageRating} votes={item.votes} />
						</div>
						<div className="distance">
							{renderIcon('pin_empty')}
							<span className="muted">
								{distance && <>{distance} • </>}
								{item.location}
							</span>
						</div>
						<div className="openingTimes">
							{renderIcon('clock')}
							<p className="p14">{formattedWorkTime}</p>
						</div>
					</div>
				</div>
				<div className="footer">
					<a
						href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
							`${item.geoLocationLatitude},${item.geoLocationLongitude}`,
						)}`}
						target="_blank"
						className="get-directions"
						rel="noreferrer"
					>
						Get directions
					</a>
				</div>
			</div>
		</div>,
	);
};

const GeoMap: React.FC<IProps<any>> = <TItem extends AnyMapItem = IMapItem>(props: IProps<TItem>) => {
	const {
		items,
		geoLatitude,
		geoLongitude,
		className,
		initLocation,
		onClick,
		onSelectClick,
		mapSettings,
		// onMarkerMouseEnter: onMouseEnter,
		// onMarkerMouseLeave: onMouseLeave,
		userPicture,
		triggerMapCenter,
	} = props;
	const holder = React.useRef<HTMLDivElement>(null);
	const map = React.useRef<google.maps.Map>(null);
	const infoWindow = React.useRef<google.maps.InfoWindow>(null);
	const markerInfoWindow = React.useRef<google.maps.InfoWindow>(null);
	const markers = React.useRef<MarkerMap[]>([]);
	const [mapHasMoved, setMapHasMoved] = React.useState(false);

	const [images, setImages] = React.useState({});

	// Move marker to the centre of the map
	React.useEffect(() => {
		if (triggerMapCenter && triggerMapCenter.currentOfficeId) {
			const currentItem = (items as AnyMapItem[])
				.filter(itemIsIOffice)
				.find((x) => x.id === triggerMapCenter?.currentOfficeId);
			if (currentItem) {
				map.current?.setCenter({ lat: currentItem.geoLocationLatitude, lng: currentItem.geoLocationLongitude });
			}
		} else if (!items.length && geoLatitude && geoLongitude) {
			map.current?.setCenter({ lat: geoLatitude, lng: geoLongitude });
		}
	}, [items]);

	// Set map centre if url lat and lng params are present on mount
	React.useEffect(() => {
		if (
			readURLSearchParams(c.SHOPS_LOCATION_FILTER_LAT) &&
			readURLSearchParams(c.SHOPS_LOCATION_FILTER_LNG) &&
			!mapHasMoved
		) {
			map.current?.setCenter({
				lat: Number(readURLSearchParams(c.SHOPS_LOCATION_FILTER_LAT)),
				lng: Number(readURLSearchParams(c.SHOPS_LOCATION_FILTER_LNG)),
			});
		}
	}, []);

	React.useEffect(() => {
		// Prevent useless calls, because multiple items have the same image
		const imagesRequested = [];
		for (const protoItem of items) {
			const item = normalizeItem(protoItem);

			if (item.image && !(item.image in images) && imagesRequested.indexOf(item.image) == -1) {
				// console.log(`item fetch: ${item.image}`)
				imagesRequested.push(item.image);
				// Prevent getting same images if they have been requested
				setImages((prevState) => ({ ...prevState, [item.image]: undefined }));

				getBase64Image(item.image).then((data) => {
					setImages((prevState) => ({ ...prevState, [item.image]: data }));
				});
			}
		}
	}, [items]);

	// Draw markers on map
	const draw = () => {
		if (!map.current) {
			return;
		}

		if (props.showInfoWindow && !markerInfoWindow.current) {
			markerInfoWindow.current = new google.maps.InfoWindow({
				pixelOffset: new google.maps.Size(0, 0),
			});
		}

		const oldRenderedJsons = (markers.current || []).map((item) => item.itemJson);
		const newRenderedJsons: typeof oldRenderedJsons = [];
		const replace: MarkerMap[] = [];
		let positionsChanged = false;
		let highlightChanged = false;

		for (const protoItem of items) {
			const item = normalizeItem(protoItem);
			const itemJson = jsonIdentifier(item);
			const icon: HTMLImageElement = document.createElement('img');
			newRenderedJsons.push(itemJson);

			if (item.image in images && images[item.image]) {
				icon.src =
					'data:image/svg+xml;charset=UTF-8;base64,' + btoa(markerFromImage(images[item.image], item.isHighlighted));
			} else {
				icon.src = 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(markerIcon(item.isHighlighted));
			}

			if (oldRenderedJsons.includes(itemJson)) {
				const existing = (markers.current || []).find((tuple) => tuple.itemJson === itemJson);

				if (existing.isHighlighted !== item.isHighlighted) {
					existing.marker.content = icon;
					highlightChanged = true;
				}
				if (existing) {
					replace.push({
						itemJson,
						marker: existing.marker,
						isHighlighted: item.isHighlighted,
					});
				}
				// TODO: fix this?
				continue;
			}

			positionsChanged = true;

			const markerOpts: google.maps.marker.AdvancedMarkerElementOptions = {
				position: { lat: item.latitude, lng: item.longitude },
				map: map.current,
				gmpClickable: Boolean(item.onClick || onClick /*|| onMouseLeave || onMouseEnter*/),
				content: icon,
				title: item.title,
			};
			const marker = new google.maps.marker.AdvancedMarkerElement(markerOpts);

			if (item.onClick || onClick) {
				marker.addListener('click', () => (item.onClick || onClick)(protoItem));
			}

			if (markerInfoWindow.current) {
				google.maps.event.addListener(markerInfoWindow.current, 'domready', () => {
					const windows = document.getElementsByClassName('markerInfoWindowWrapper');
					for (let i = 0; i < windows.length; i++) {
						windows[i].addEventListener('mouseleave', () => {
							setTimeout(() => {
								markerInfoWindow.current?.close();
							}, 100);
						});
					}
				});
			}

			if (onClick) {
				// When clicking on marker
				marker.addListener('click', () => {
					if (protoItem?.isFromShopPage && markerInfoWindow.current && isOffice(protoItem) && 'shop' in protoItem) {
						markerInfoWindow.current.setContent(getMarkerInfoWindowContent(protoItem));
						markerInfoWindow.current.open(map.current);
						markerInfoWindow.current.setPosition(new google.maps.LatLng(item.latitude, item.longitude));
					}
					return onClick(protoItem);
				});
				// When clicking on map
				google.maps.event.addListener(map.current, 'click', () => {
					markerInfoWindow.current.close();
				});
			}

			// // Google maps' "addListener" function does currently not support below event for Advanced Markers, see supported events below
			// // https://developers.google.com/maps/documentation/javascript/reference/advanced-markers
			// if (onMouseEnter) {
			// 	marker.addEventListener('mouseover', () => {
			// 		if (protoItem?.isFromShopPage && markerInfoWindow.current && isOffice(protoItem) && 'shop' in protoItem) {
			// 			markerInfoWindow.current.setContent(getMarkerInfoWindowContent(protoItem));
			// 			markerInfoWindow.current.open(map.current);
			// 			markerInfoWindow.current.setPosition(new google.maps.LatLng(item.latitude, item.longitude));
			// 		}
			// 		return onMouseEnter(protoItem);
			// 	});
			// }

			// // Google maps' "addListener" function does currently not support below event for Advanced Markers, see supported events below
			// // https://developers.google.com/maps/documentation/javascript/reference/advanced-markers
			// if (onMouseLeave) {
			// 	marker.content.addEventListener('mouseout', () => {
			// 		return onMouseLeave(protoItem);
			// 	});
			// }

			replace.push({
				itemJson,
				marker,
				isHighlighted: item.isHighlighted,
			});
		}

		for (const mm of markers.current) {
			if (newRenderedJsons.includes(mm.itemJson)) {
				if (!replace.some((t) => t.itemJson === mm.itemJson)) {
					replace.push(mm);
				}
			} else {
				mm.marker.map = null; // Remove marker when new search was triggered
				positionsChanged = true;
			}
		}

		if (positionsChanged || highlightChanged) {
			markers.current = replace;
		}

		if (positionsChanged) {
			// Pan map to show all markers when they change
			if (markers.current.length) {
				const bounds = new google.maps.LatLngBounds();
				markers.current.forEach((tuple) => {
					bounds.extend(tuple.marker.position);
				});
				map.current.fitBounds(bounds);
				setTimeout(() => {
					try {
						map.current?.fitBounds(bounds);
					} catch (e) {
						console.error(e);
					}
				}, 100);
				setTimeout(() => {
					if (props.onMapDrawn && map.current) {
						props.onMapDrawn(map.current.getBounds());
					}
				}, 100);
			}
		}
	};

	// Init map when holder changes
	React.useEffect(() => {
		markers.current = [];
		const urlLat = readURLSearchParams(c.SHOPS_LOCATION_FILTER_LAT);
		const urlLng = readURLSearchParams(c.SHOPS_LOCATION_FILTER_LNG);
		map.current = new google.maps.Map(holder.current, {
			// styles: mapStyles, // cannot be used with mapId
			zoom: 10,
			center: urlLat && urlLng ? { lat: Number(urlLat), lng: Number(urlLng) } : initLocation || { lat: 0, lng: 0 },
			mapTypeControl: false, // disable Map & Satellite buttons
			streetViewControl: false,
			...(mapSettings || {}),
			mapId: '14db460f80f003e8', // needed for marker and controls the styling through Google Maps Platform
		});
		infoWindow.current = new google.maps.InfoWindow();
		map.current.addListener('bounds_changed', () => {
			if (props.onMapMove) {
				props.onMapMove(map.current.getBounds());
				setMapHasMoved(true);
			}
		});
		const generateMapCeterCoords = () => ({
			lat: map.current.getCenter().lat(),
			lng: map.current.getCenter().lng(),
		});

		/////////////////////////////////////////
		// Add filter button
		/////////////////////////////////////////
		if (props.openFiltersModal) {
			const filterButton = document.createElement('button');

			filterButton.classList.add('filterButton', 'pointer');
			// filterButton.title = i18n.t('GeoMap_Filter');
			filterButton.type = 'button';

			if (props.numberOfActiveFilters > 0) {
				const numberOfFilters = document.createElement('div');
				numberOfFilters.classList.add('numberOfFilters');
				numberOfFilters.innerHTML = `${props.numberOfActiveFilters}`;
				filterButton.appendChild(numberOfFilters);
			}
			map.current.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(filterButton);

			filterButton.addEventListener('click', () => {
				props.openFiltersModal();
			});
		}

		/////////////////////////////////////////
		// Add locate-me button
		/////////////////////////////////////////
		const locationButton = document.createElement('button');
		locationButton.textContent = '\u00A0';
		locationButton.classList.add('locateMeButton', 'pointer');
		locationButton.title = i18n.t('GeoMap_LocateMe');
		locationButton.type = 'button'; // For some reason, default button type is submit
		/* Button position */
		map.current.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(locationButton);
		/* Button click action */
		locationButton.addEventListener('click', () => {
			// Try HTML5 geolocation.
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(
					(position: any) => {
						// type GeolocationPostion has typeerrors
						const pos = {
							lat: position.coords.latitude,
							lng: position.coords.longitude,
						};
						infoWindow.current.setPosition(pos);
						infoWindow.current.open(map.current);

						// show close button
						google.maps.event.addListener(infoWindow.current, 'domready', () => {
							(document.querySelector('.gm-style-iw-t button[title=Close]') as HTMLElement).title = 'Close show';
						});
						///////////////////////////////////////////
						// Custom marker - user photo
						///////////////////////////////////////////
						const locationUserMarker = document.createElement('img');
						locationUserMarker.src = userPicture;
						locationUserMarker.classList.add('locationUserMarker');
						infoWindow.current.setContent(locationUserMarker);
						///////////////////////////////////////////
						map.current.setCenter(pos);
					},
					() => {
						handleLocationError(true);
					},
				);
			} else {
				// Browser doesn't support Geolocation
				handleLocationError(false);
			}
		});
		/* Button click location error */
		function handleLocationError(browserHasGeolocation: boolean) {
			const message = document.createElement('p');
			const generateMessage = () => {
				return browserHasGeolocation
					? i18n.t('GeoMap_LocationError_NotEnabled')
					: i18n.t('GeoMap_LocationError_NotSupported');
			};
			message.textContent = generateMessage();
			message.classList.add('geoMapError');
			infoWindow.current.setPosition(generateMapCeterCoords());
			infoWindow.current.setContent(message);
			infoWindow.current.open(map.current);
		}
		/////////////////////////////////////////

		const isShopPage: boolean =
			(items?.length && Object.prototype.hasOwnProperty.call(items[0], 'isFromShopPage')) || false;

		if (isShopPage) {
			/////////////////////////////////////////
			// Add select-salon button
			/////////////////////////////////////////
			const item: any = items[0];

			const changeLocationButton = document.createElement('button');
			changeLocationButton.textContent = i18n.t('GeoMap_SelectThisSalon');
			changeLocationButton.classList.add(
				'btnBottom',
				'btnPrimary',
				'bsPortalBtn',
				'bsPortalBtnLarge',
				'pointer',
				'geoMapSelectSalonButton',
			);
			changeLocationButton.title = 'Change salon';
			changeLocationButton.type = 'button';
			changeLocationButton.addEventListener('click', () => {
				if (onSelectClick) {
					onSelectClick();
				}
				if (document.exitFullscreen) {
					document.exitFullscreen();
				}
			});
			map.current.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(changeLocationButton);
			/////////////////////////////////////////

			const salonsName = document.createElement('div');
			salonsName.textContent = `${item?.shop.name} Salons`;
			salonsName.classList.add('geoMapSalonsName');
			map.current.controls[google.maps.ControlPosition.TOP_LEFT].push(salonsName);

			const buttonClose = document.createElement('img');
			buttonClose.src = require('containers/Html/icons/cancel_search.svg');
			buttonClose.classList.add('geoMapCloseButton');
			map.current.controls[google.maps.ControlPosition.TOP_CENTER].push(buttonClose);
		}

		draw();
		return destroyMap(map.current);
	}, [holder, JSON.stringify(mapSettings), userPicture, images, props.numberOfActiveFilters]);

	React.useEffect(() => {
		if (!map.current || markers.current?.length) {
			return;
		}
		if (!readURLSearchParams(c.SHOPS_LOCATION_FILTER_LAT) && !readURLSearchParams(c.SHOPS_LOCATION_FILTER_LNG)) {
			map.current.setCenter(initLocation);
		}
	}, [initLocation]);

	// Redraw markers when items or map change
	React.useEffect(draw, [JSON.stringify(items), map.current, map]);

	return <div ref={holder} className={'geoMap ' + (className || '')}></div>;
};

export { GeoMap };
